import { CNPJMaskedInput } from "../../../../../../common/components/masked_inputs/cnpj_masked_input";
import { CPFMaskedInput } from "../../../../../../common/components/masked_inputs/cpf_masked_input";
import { DefaultSelectInput } from "../../../../../../common/components/default_select_input";
import { useMemo } from "react";
import { CentroCustoDropDown } from "../../../../../../common/domain/models/centro_custo_dropdown";
import { DefaultMultiAutocomplete } from "../../../../../../common/components/default_multi_autocomplete";
import { FornecedorFamiliaDropDown } from "../../../../domain/models/fornecedor_familia_dropdown";
import { FornecedorAtividadeDropDown } from "../../../../domain/models/fornecedor_atividade_dropdown";

import CascateList from "../../../../../../common/components/cascate_list"
import DefaultInput from "../../../../../../common/components/default_input";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import PrimaryButton from "../../../../../../common/components/primary_button";

/**
 * Header
 */

type Props = {
  razaoSocial: string;
  cnpj: string;
  cpf: string;
  nomeFantasia: string;
  centroCustos: number[] | null;
  familias: string[] | null;
  atividades: string[] | null;
  documento: number;
  tipoFornecedor: number;
  status: number;
  avaliacaoCentroCusto: number[] | null;
  centroCustoDropdown: CentroCustoDropDown[];
  familiasDropdown: FornecedorFamiliaDropDown[];
  atividadesDropdown: FornecedorAtividadeDropDown[];
  onRazaoSocialChange: (value: string) => void;
  onCnpjChange: (value: string) => void;
  onCpfChange: (value: string) => void;
  onNomeFantasiaChange: (value: string) => void;
  onCentroCustosChange: (value: number[] | null) => void;
  onFamiliasChange: (value: string[] | null) => void;
  onAtividadesChange: (value: string[] | null) => void;
  onDocumentoChange: (value: number) => void;
  onTipoFornecedorChange: (value: number) => void;
  onStatusChange: (value: number) => void;
  onAvaliacaoCentroCustoChange: (value: number[] | null) => void;
  onClearFilters: () => void;
};

export const Header = ({
  razaoSocial,
  cnpj,
  cpf,
  nomeFantasia,
  centroCustos,
  familias,
  atividades,
  documento,
  tipoFornecedor,
  status,
  avaliacaoCentroCusto,
  centroCustoDropdown,
  familiasDropdown,
  atividadesDropdown,
  onRazaoSocialChange,
  onCnpjChange,
  onCpfChange,
  onNomeFantasiaChange,
  onCentroCustosChange,
  onFamiliasChange,
  onAtividadesChange,
  onDocumentoChange,
  onTipoFornecedorChange,
  onStatusChange,
  onAvaliacaoCentroCustoChange,
  onClearFilters,
}: Props) => {
  const selectedCentroCustos = useMemo(() => {
    if (centroCustos === null) return [];
    return centroCustoDropdown.filter(x => centroCustos.some(y => y === x.id));
  }, [
    centroCustos,
    centroCustoDropdown,
  ]);

  const selectedFamilias = useMemo(() => {
    if (familias === null) return [];
    return familiasDropdown.filter(x => familias.some(y => y === x.familia));
  }, [
    familiasDropdown,
    familias,
  ]);

  const selectedAtividades = useMemo(() => {
    if (atividades === null) return [];
    return atividadesDropdown.filter(x => atividades.some(y => y === x.atividade));
  }, [
    atividadesDropdown,
    atividades,
  ]);

  const selectedAvaliacaoCentroCustos = useMemo(() => {
    if (avaliacaoCentroCusto === null) return [];
    return centroCustoDropdown.filter(x => avaliacaoCentroCusto.some(y => y === x.id));
  }, [
    avaliacaoCentroCusto,
    centroCustoDropdown,
  ]);

  const documentoOptions = useMemo(() => {
    return [
      {
        id: 1,
        label: 'Todos',
      },
      {
        id: 2,
        label: 'Vencidos',
      },
      {
        id: 3,
        label: 'Em dia',
      },
    ];
  }, [
  ]);

  const tipoFornecedorOptions = useMemo(() => {
    return [
      {
        id: 1,
        label: 'Todos',
      },
      {
        id: 2,
        label: 'Produtos',
      },
      {
        id: 3,
        label: 'Serviços',
      },
    ];
  }, [
  ]);

  const statusOptions = useMemo(() => {
    return [
      {
        id: 1,
        label: 'Todos',
      },
      {
        id: 2,
        label: 'Ativos',
      },
      {
        id: 3,
        label: 'Inativos',
      },
    ];
  }, [
  ]);

  return (
    <CascateList title='Filtros' defaultOpen={true}>
      <div className="w-full flex flex-col gap-4">
        <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <DefaultInput
            name='razaoSocial'
            label='Razão Social'
            value={razaoSocial}
            onChangeValue={onRazaoSocialChange}
          />

          <CNPJMaskedInput
            name='cnpj'
            label='Cnpj'
            value={cnpj}
            unmask={true}
            onChange={onCnpjChange}
          />

          <CPFMaskedInput
            name='cpf'
            label='CPF'
            value={cpf}
            unmask={true}
            onChange={onCpfChange}
          />

          <DefaultInput
            name='nomeFantasia'
            label='Nome Fantasia'
            value={nomeFantasia}
            onChangeValue={onNomeFantasiaChange}
          />

          <DefaultMultiAutocomplete
            label='Centro de Custo'
            options={centroCustoDropdown}
            getOptionKey={x => x.id}
            getOptionLabel={x => x.title}
            value={selectedCentroCustos}
            onChange={v => onCentroCustosChange(v ? v.map(x => x.id) : null)}
          />

          <DefaultMultiAutocomplete
            label='Família'
            options={familiasDropdown}
            getOptionKey={x => x.familia}
            getOptionLabel={x => x.familia}
            value={selectedFamilias}
            onChange={e => onFamiliasChange(e ? e.map(x => x.familia) : null)}
          />

          <DefaultMultiAutocomplete
            label='Atividade'
            options={atividadesDropdown}
            getOptionKey={x => x.atividade}
            getOptionLabel={x => x.atividade}
            value={selectedAtividades}
            onChange={e => onAtividadesChange(e ? e.map(x => x.atividade) : null)}
          />

          <DefaultSelectInput
            name='documento'
            label='Documento'
            showSelectionLabel={false}
            options={documentoOptions}
            value={documento}
            onChangeValue={e => onDocumentoChange(Number(e))}
          />

          <DefaultSelectInput
            name='tipoFornecedor'
            label='Tipo de Fornecedor'
            showSelectionLabel={false}
            options={tipoFornecedorOptions}
            value={tipoFornecedor}
            onChangeValue={e => onTipoFornecedorChange(Number(e))}
          />

          <DefaultSelectInput
            name='status'
            label='Status'
            showSelectionLabel={false}
            options={statusOptions}
            value={status}
            onChangeValue={e => onStatusChange(Number(e))}
          />

          <div className="md:col-span-2 lg:col-span-3">
            <DefaultMultiAutocomplete
              label='Avaliação de fornecedor por centro de custo'
              options={centroCustoDropdown}
              getOptionKey={x => x.id}
              getOptionLabel={x => x.title}
              value={selectedAvaliacaoCentroCustos}
              onChange={v => onAvaliacaoCentroCustoChange(v ? v.map(x => x.id) : null)}
            />
          </div>
        </div>

        <div className="w-full flex justify-end">
          <div className="w-[180px]">
            <PrimaryButton
              startIcon={<DynamicIcon nameIcon="ClearRounded" size={25} className="text-black" />}
              buttonColor="yellow"
              onClick={onClearFilters}>
              Limpar filtros
            </PrimaryButton>
          </div>
        </div>
      </div>
    </CascateList>
  );
}